// frontend/src/pages/portal-admin/ProductCategoriesManagement.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PortalAdminLayout from '../admin/PortalAdminLayout';
import './PortalAdminStyles.css';

const ProductCategoriesManagement = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [formData, setFormData] = useState({
    value: '',
    label: '',
    icon: '',
    description: '',
    category: 'other',
    active: true
  });

  const categoryGroups = [
    { value: 'hair', label: 'Hair Services' },
    { value: 'nails', label: 'Nail Services' },
    { value: 'spa', label: 'Spa & Skincare' },
    { value: 'massage', label: 'Massage & Bodywork' },
    { value: 'makeup', label: 'Makeup & Beauty' },
    { value: 'body', label: 'Body Treatments' },
    { value: 'wellness', label: 'Wellness & Holistic' },
    { value: 'fitness', label: 'Fitness & Training' },
    { value: 'professional', label: 'Professional Supplies' },
    { value: 'retail', label: 'Retail Products' },
    { value: 'other', label: 'Other' }
  ];

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('ogestyle_token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/product-categories/all`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCategories(response.data || []);
    } catch (error) {
      alert('Failed to fetch product categories');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = () => {
    setEditingCategory(null);
    setFormData({
      value: '',
      label: '',
      icon: '',
      description: '',
      category: 'other',
      active: true
    });
    setShowAddModal(true);
  };

  const handleEdit = (cat) => {
    setEditingCategory(cat.value);
    setFormData({
      value: cat.value,
      label: cat.label,
      icon: cat.icon || '',
      description: cat.description || '',
      category: cat.category || 'other',
      active: cat.active !== false
    });
    setShowAddModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('ogestyle_token');
      const url = editingCategory
        ? `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/product-categories/${editingCategory}`
        : `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/product-categories`;
      
      const method = editingCategory ? 'put' : 'post';
      
      await axios[method](url, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setShowAddModal(false);
      fetchCategories();
      alert(`Product category ${editingCategory ? 'updated' : 'created'} successfully!`);
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to save product category');
    }
  };

  const handleDelete = async (value) => {
    if (!window.confirm('Are you sure you want to delete this product category?')) {
      return;
    }
    
    try {
      const token = localStorage.getItem('ogestyle_token');
      await axios.delete(
        `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/product-categories/${value}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchCategories();
      alert('Product category deleted successfully!');
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to delete product category');
    }
  };

  const toggleActive = async (cat) => {
    try {
      const token = localStorage.getItem('ogestyle_token');
      await axios.put(
        `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/product-categories/${cat.value}`,
        { ...cat, active: !cat.active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      fetchCategories();
    } catch (error) {
      alert('Failed to toggle category status');
    }
  };

  const handleExport = async () => {
    try {
      const token = localStorage.getItem('ogestyle_token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/product-categories/export`,
        { 
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `product-categories-export-${new Date().toISOString().split('T')[0]}.json`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      alert('Failed to export product categories');
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const text = await file.text();
      const data = JSON.parse(text);
      
      if (!data.productCategories || !Array.isArray(data.productCategories)) {
        alert('Invalid import file format');
        return;
      }

      const mode = window.confirm(
        `Import ${data.productCategories.length} categories.\n\n` +
        `Click OK to MERGE with existing (recommended)\n` +
        `Click Cancel to REPLACE all existing categories`
      ) ? 'merge' : 'replace';

      const token = localStorage.getItem('ogestyle_token');
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/api/platform-settings/product-categories/import`,
        { productCategories: data.productCategories, mode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      alert(`Successfully imported ${data.productCategories.length} categories (${mode} mode)`);
      fetchCategories();
    } catch (error) {
      alert(error.response?.data?.error || 'Failed to import product categories');
    }
    
    e.target.value = '';
  };

  if (loading) {
    return (
      <PortalAdminLayout>
        <div className="portal-admin-loading">Loading...</div>
      </PortalAdminLayout>
    );
  }

  return (
    <PortalAdminLayout>
      <div className="portal-admin-container">
      <div className="portal-admin-header">
        <div>
          <h1 className="portal-admin-title">Product Categories</h1>
          <p className="portal-admin-subtitle">
            Manage product categories for e-commerce ({categories.length} total)
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px' }}>
          <label className="btn-secondary" style={{ cursor: 'pointer' }}>
            📥 Import
            <input 
              type="file" 
              accept=".json" 
              onChange={handleImport} 
              style={{ display: 'none' }}
            />
          </label>
          <button className="btn-secondary" onClick={handleExport}>
            📤 Export
          </button>
          <button className="btn-primary" onClick={openAddModal}>
            + Add Product Category
          </button>
        </div>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {categories.map(cat => (
          <div key={cat.value} style={{
            display: 'flex',
            alignItems: 'center',
            background: 'white',
            border: '1px solid #e5e7eb',
            borderRadius: '8px',
            padding: '16px 20px',
            gap: '16px'
          }}>
            <div style={{ fontSize: '1.5rem' }}>{cat.icon}</div>
            
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '4px' }}>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1a1a1a' }}>
                  {cat.label}
                </h3>
                <span style={{
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  fontWeight: '600',
                  background: cat.active ? '#10b981' : '#ef4444',
                  color: 'white'
                }}>
                  {cat.active ? 'ACTIVE' : 'INACTIVE'}
                </span>
                <span style={{
                  padding: '2px 8px',
                  borderRadius: '4px',
                  fontSize: '11px',
                  background: '#f3f4f6',
                  color: '#6b7280'
                }}>
                  {categoryGroups.find(g => g.value === cat.category)?.label || cat.category}
                </span>
              </div>
              
              {cat.description && (
                <p style={{ margin: '4px 0', fontSize: '13px', color: '#666' }}>{cat.description}</p>
              )}
              
              <span style={{ fontSize: '11px', color: '#999', fontFamily: 'monospace' }}>
                ID: {cat.value}
              </span>
            </div>
            
            <div style={{ display: 'flex', gap: '8px', marginLeft: 'auto' }}>
              <button 
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  background: cat.active ? '#fef3c7' : '#d1fae5',
                  color: cat.active ? '#92400e' : '#065f46'
                }}
                onClick={() => toggleActive(cat)}
              >
                {cat.active ? 'Deactivate' : 'Activate'}
              </button>
              <button 
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  background: '#dbeafe',
                  color: '#1e40af'
                }}
                onClick={() => handleEdit(cat)}
              >
                Edit
              </button>
              <button 
                style={{
                  padding: '6px 12px',
                  borderRadius: '6px',
                  border: 'none',
                  fontSize: '13px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  background: '#fee2e2',
                  color: '#991b1b'
                }}
                onClick={() => handleDelete(cat.value)}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {showAddModal && (
        <div className="modal-overlay" onClick={() => setShowAddModal(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2 className="modal-title">
              {editingCategory ? 'Edit Product Category' : 'Add Product Category'}
            </h2>
            
            <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label>Value (ID) *</label>
                <input
                  type="text"
                  value={formData.value}
                  onChange={(e) => setFormData({ ...formData, value: e.target.value })}
                  placeholder="e.g., hair-care"
                  required
                  disabled={editingCategory}
                />
              </div>

              <div className="form-group">
                <label>Label *</label>
                <input
                  type="text"
                  value={formData.label}
                  onChange={(e) => setFormData({ ...formData, label: e.target.value })}
                  placeholder="e.g., Hair Care"
                  required
                />
              </div>

              <div className="form-group">
                <label>Icon (Emoji)</label>
                <input
                  type="text"
                  value={formData.icon}
                  onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
                  placeholder="e.g., 🧴"
                  maxLength="4"
                />
              </div>

              <div className="form-group">
                <label>Category Group</label>
                <select
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                >
                  {categoryGroups.map(group => (
                    <option key={group.value} value={group.value}>{group.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description..."
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  />
                  Active
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => setShowAddModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  {editingCategory ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </div>
    </PortalAdminLayout>
  );
};

export default ProductCategoriesManagement;
